/**
 * Cloudflare Turnstile, invisible, for the ad landing page only.
 *
 * The site key comes from VITE_TURNSTILE_SITE_KEY at build time. Blank or unset
 * means no Turnstile at all: no script, no widget, and the sign-up sends no
 * token. The edge function's secret is set separately; with either one missing,
 * every sign-up passes exactly as before.
 *
 * The widget's mode (invisible) is set on the site key in Cloudflare's
 * dashboard. A token is single-use, so the widget is reset after each send.
 * The server never refuses a failed check: it marks the row instead, so a
 * visitor whose browser blocks Cloudflare still gets signed up.
 */

import { useCallback, useEffect, useRef } from "react";

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
// How long a send waits for a token that has not arrived yet. Past this the
// sign-up goes without one and the server marks it rather than losing it.
const TOKEN_WAIT_MS = 4_000;

export function turnstileSiteKey(): string | null {
  const raw = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim();
  return raw ? raw : null;
}

let scriptPromise: Promise<TurnstileApi | null> | null = null;

function loadScript(): Promise<TurnstileApi | null> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.turnstile ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Renders the invisible widget into the returned ref and hands back a
 * `getToken` that resolves to the current token, or null when Turnstile is off,
 * blocked, or too slow.
 */
export function useTurnstile() {
  const ref = useRef<HTMLDivElement>(null);
  const token = useRef<string | null>(null);
  const widgetId = useRef<string | null>(null);
  const waiters = useRef<Array<(t: string | null) => void>>([]);

  useEffect(() => {
    const sitekey = turnstileSiteKey();
    if (!sitekey) return;
    let cancelled = false;

    loadScript().then((api) => {
      if (cancelled || !api || !ref.current) return;
      widgetId.current = api.render(ref.current, {
        sitekey,
        callback: (t) => {
          token.current = t;
          waiters.current.splice(0).forEach((resolve) => resolve(t));
        },
        "expired-callback": () => {
          token.current = null;
        },
        "error-callback": () => {
          token.current = null;
        },
      });
    });

    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!turnstileSiteKey()) return null;
    if (token.current) return token.current;
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), TOKEN_WAIT_MS);
      waiters.current.push((t) => {
        clearTimeout(timer);
        resolve(t);
      });
    });
  }, []);

  /** Tokens are single-use: get a fresh one ready after each send. */
  const reset = useCallback(() => {
    token.current = null;
    if (widgetId.current) window.turnstile?.reset(widgetId.current);
  }, []);

  return { ref, getToken, reset };
}
